// AI Wallet — combined calling + texting credits.
//
// Layout mirrors the brief:
//   - Header card (balance + low-balance ribbon + pricing)
//   - Add funds row ($50/$100/$250/$500 + custom, min $50 first load)
//   - Auto-reload toggle + threshold/amount + status
//   - Activity ledger (last 50 rows)
//   - Demo simulation buttons (gated to SFA_DEMO_WALLET=true on server)

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { Wallet, Zap, CheckCircle, AlertTriangle, Settings, RefreshCcw, ChevronDown } from 'lucide-react';
import { supabase } from '../lib/supabase';

async function apiFetch(path, opts = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...opts,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(opts.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
  return body;
}

const fmtUSD = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;
const fmtAgo = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// Tweens the displayed balance from its previous value to the new
// target over ~600ms with an ease-out curve. Effect: when a debit
// lands, the operator SEES the number count down — the demo's
// "watch it spend" promise made literal.
function useTweenedBalance(targetCents) {
  const [displayed, setDisplayed] = React.useState(targetCents);
  const fromRef = React.useRef(targetCents);
  React.useEffect(() => {
    const start = fromRef.current;
    const delta = targetCents - start;
    if (delta === 0) return;
    const startTime = performance.now();
    const duration = 700;
    let rafId;
    const step = (t) => {
      const p = Math.min(1, (t - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.round(start + delta * eased);
      setDisplayed(next);
      fromRef.current = next;
      if (p < 1) rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
  }, [targetCents]);
  return displayed;
}

// Flash a brief glow on the balance when it changes — red on debit,
// emerald on credit. Returns a className suffix.
function useChangeFlash(value) {
  const [tone, setTone] = React.useState(null);
  const prevRef = React.useRef(value);
  React.useEffect(() => {
    if (prevRef.current !== value) {
      const dir = value < prevRef.current ? 'debit' : 'credit';
      setTone(dir);
      const id = setTimeout(() => setTone(null), 600);
      prevRef.current = value;
      return () => clearTimeout(id);
    }
  }, [value]);
  return tone;
}

// Sum the dollar cost of an ordered list of {kind, units} steps.
function scenarioCostCents(steps, pricing) {
  return steps.reduce((s, st) => s + st.units * (st.kind === 'sms' ? pricing.sms_per_msg_cents : pricing.call_per_min_cents), 0);
}

// Real-world agent moments — each maps to a sequence of SMS + call
// debits that fire as separate ledger rows with 350ms drum-roll
// pacing. Lets the prospect see "this is what closing a final-expense
// lead actually costs in AI minutes."
const AGENT_SCENARIOS = [
  {
    key: 'welcome', emoji: '👋', title: 'Welcome a new client',
    desc: 'Day-0 + day-3 SMS, plus a 2-min courtesy call',
    steps: [
      { kind: 'sms',  units: 1, label: 'Day-0 welcome text' },
      { kind: 'call', units: 2, label: '2-min courtesy call' },
      { kind: 'sms',  units: 1, label: 'Day-3 follow-up text' },
    ],
  },
  {
    key: 'reminder', emoji: '💳', title: 'Draft date cadence',
    desc: '3 SMS: T-3, T-1, day-of',
    steps: [
      { kind: 'sms', units: 1, label: 'T-3 reminder' },
      { kind: 'sms', units: 1, label: 'T-1 reminder' },
      { kind: 'sms', units: 1, label: 'Day-of reminder' },
    ],
  },
  {
    key: 'close', emoji: '📞', title: 'Close a final-expense lead',
    desc: '8 qualifier texts + 15-min discovery call',
    steps: [
      { kind: 'sms',  units: 4,  label: 'Qualifier texts (round 1)' },
      { kind: 'sms',  units: 4,  label: 'Follow-up texts (round 2)' },
      { kind: 'call', units: 15, label: '15-min discovery call' },
    ],
  },
  {
    key: 'save', emoji: '🛟', title: 'Save a lapsing policy',
    desc: '4 urgency SMS + 5-min retention call',
    steps: [
      { kind: 'sms',  units: 4, label: 'Save cadence texts' },
      { kind: 'call', units: 5, label: '5-min retention call' },
    ],
  },
];

export default function AiWalletView({ currentUser, showToast = () => {} }) {
  return <WalletPanel showToast={showToast}/>;
}

function WalletPanel({ showToast }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [custom, setCustom]   = React.useState('');
  const [busy, setBusy]       = React.useState(false);
  const [autoRel, setAutoRel] = React.useState(null);   // local edit buffer
  const [showSettings, setShowSettings] = React.useState(false);

  const [loadError, setLoadError] = React.useState(null);
  const load = React.useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // 15s timeout so a hung request surfaces an actionable error
    // instead of leaving the operator staring at "Loading AI Wallet…".
    // Reported 2026-06-27: stuck loader on Settings → AI & Automation.
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 15000);
    try {
      const r = await apiFetch('/api/wallet', { signal: ctrl.signal });
      setData(r);
      setAutoRel({
        enabled:   r.auto_topup_enabled,
        threshold: Math.round((r.low_threshold_cents || 1500) / 100),
        amount:    Math.round((r.auto_topup_amount_cents || 10000) / 100),
      });
    } catch (err) {
      const msg = err.name === 'AbortError' ? 'Request timed out after 15s.' : (err.message || String(err));
      setLoadError(msg);
      showToast(`Wallet load failed: ${msg}`);
    }
    finally { clearTimeout(timer); setLoading(false); }
  }, [showToast]);
  React.useEffect(() => { load(); }, [load]);

  const topUp = async (amountCents) => {
    if (!amountCents || amountCents < 100) { showToast('Enter an amount.'); return; }
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet/topup', { method: 'POST', body: JSON.stringify({ amount_cents: amountCents }) });
      if (r.url) window.location.assign(r.url);
    } catch (err) { showToast(`Top-up failed: ${err.message}`); setBusy(false); }
  };

  const saveSettings = async () => {
    if (!autoRel) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet', {
        method: 'POST',
        body: JSON.stringify({
          action: 'update_settings',
          auto_topup_enabled:           autoRel.enabled,
          credits_low_threshold_cents:  Math.round(autoRel.threshold * 100),
          auto_topup_amount_cents:      Math.round(autoRel.amount    * 100),
        }),
      });
      setData(r);
      showToast('Auto-reload saved.');
    } catch (err) { showToast(`Save failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const testAutoReload = async () => {
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet/auto-reload', { method: 'POST', body: JSON.stringify({ action: 'trigger' }) });
      showToast(`Auto-reload ${r.status === 'succeeded' ? 'succeeded' : `status=${r.status}`} — webhook will credit shortly.`);
      setTimeout(load, 2500);
    } catch (err) { showToast(`Auto-reload failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  // Confirmation guard around any debit. Prevents accidental drains
  // from misclicks while scrolling / tab-switching (BUG-003). All
  // simulated debits ARE real money — Reset Demo can refund them,
  // but the click should be explicit.
  const confirmSpend = (label, costCents) => {
    if (!data?.balance_cents && data?.balance_cents !== 0) return true;
    const cost = fmtUSD(costCents);
    const after = fmtUSD(Math.max(0, data.balance_cents - costCents));
    return window.confirm(
      `Spend ${cost} on "${label}"?\n\n` +
      `Balance after: ${after}\n\n` +
      `This deducts from your AI Wallet. Click Cancel to abort.`
    );
  };

  const simulate = async (kind, units) => {
    const costCents = kind === 'sms'
      ? units * (data?.pricing?.sms_per_msg_cents || 0)
      : units * (data?.pricing?.call_per_min_cents || 0);
    const label = kind === 'call' ? `${units}-min call` : `${units} text${units === 1 ? '' : 's'}`;
    if (!confirmSpend(label, costCents)) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate', kind, units }) });
      setData(r);
      showToast(`Simulated ${label}.`);
    } catch (err) { showToast(`Simulate failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  // Fires a sequence of simulate calls SERIALLY with a 350ms gap so
  // the operator watches the ledger fill row-by-row. Per-step toast
  // narrates the spend in plain English. Used by AGENT_SCENARIOS.
  const runScenario = async (scenario) => {
    const totalCents = scenarioCostCents(scenario.steps, data.pricing);
    if (!confirmSpend(scenario.title, totalCents)) return;
    setBusy(true);
    try {
      for (let i = 0; i < scenario.steps.length; i++) {
        const s = scenario.steps[i];
        const r = await apiFetch('/api/wallet', {
          method: 'POST',
          body: JSON.stringify({ action: 'simulate', kind: s.kind, units: s.units }),
        });
        setData(r);
        const perUnit = s.kind === 'sms' ? r.pricing.sms_per_msg_cents : r.pricing.call_per_min_cents;
        showToast(`${s.label} · -${fmtUSD(s.units * perUnit)}`);
        if (i < scenario.steps.length - 1) await new Promise(res => setTimeout(res, 350));
      }
      showToast(`${scenario.title} ✓ — total ${fmtUSD(scenarioCostCents(scenario.steps, data.pricing))}.`);
    } catch (err) {
      if (/insufficient_credits|HTTP 402/.test(err.message)) {
        showToast('Hard stop fired — wallet rejected the next debit. Top up to continue.');
      } else {
        showToast(`Scenario failed: ${err.message}`);
      }
    } finally { setBusy(false); }
  };

  // Drop the balance to just under the low-balance threshold in ONE
  // batched SMS debit so the operator sees the amber Low-balance
  // banner light up immediately.
  const drainToLow = async () => {
    if (data.balance_cents <= data.low_threshold_cents) {
      showToast('Already at or below the low-balance threshold.');
      return;
    }
    const perMsg = data.pricing.sms_per_msg_cents;
    const overshoot = perMsg * 2;
    const dropCents = data.balance_cents - data.low_threshold_cents + overshoot;
    const units = Math.max(1, Math.ceil(dropCents / perMsg));
    if (!confirmSpend(`Drain to low-balance threshold (${units} texts)`, units * perMsg)) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate', kind: 'sms', units }) });
      setData(r);
      showToast(`Drained ${units} texts (${fmtUSD(units * perMsg)}) · low-balance state active.`);
    } catch (err) { showToast(`Drain failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  // Drain to ~$0 in one debit, then attempt one MORE call → server
  // responds 402 with insufficient_credits and we surface the
  // production hard-stop UX as a toast.
  const drainPastZero = async () => {
    if (!confirmSpend('Drain wallet past zero (hard-stop demo)', data.balance_cents)) return;
    setBusy(true);
    try {
      const perMin = data.pricing.call_per_min_cents;
      if (data.balance_cents > 0) {
        const drainMin = Math.floor(data.balance_cents / perMin);
        if (drainMin > 0) {
          const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate', kind: 'call', units: drainMin }) });
          setData(r);
          showToast(`Balance drained to ${fmtUSD(r.balance_cents)}.`);
          await new Promise(res => setTimeout(res, 500));
        }
      }
      // One more debit — production behavior is to refuse.
      try {
        const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate', kind: 'call', units: 1 }) });
        setData(r);
        showToast('Server allowed the debit — expected a hard stop. Check pricing config.');
      } catch (err) {
        if (/insufficient_credits|HTTP 402/.test(err.message)) {
          showToast('Hard stop ✓ — wallet rejected the next call. Production behaves the same.');
        } else { throw err; }
      }
      await load();
    } catch (err) { showToast(`Hard-stop demo failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  // Auto-reload demo: drain below the threshold and call the trigger
  // endpoint so the saved card runs and the +load lands in the ledger.
  const triggerAutoReload = async () => {
    if (!data.has_payment_method) {
      showToast('Add a card first (Top up once to save it on file).');
      return;
    }
    if (!data.auto_topup_enabled) {
      showToast('Turn on Auto-reload in the Settings panel below, then retry.');
      return;
    }
    if (!window.confirm('Trigger an auto-reload charge on your saved card now? This will charge your card and credit the wallet via the Stripe webhook.')) return;
    setBusy(true);
    try {
      const perMsg = data.pricing.sms_per_msg_cents;
      const target = Math.max(0, data.low_threshold_cents - perMsg * 3);
      const dropBy = data.balance_cents - target;
      if (dropBy > 0) {
        const units = Math.ceil(dropBy / perMsg);
        const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate', kind: 'sms', units }) });
        setData(r);
        showToast(`Drained below auto-reload threshold (${fmtUSD(r.balance_cents)}).`);
        await new Promise(res => setTimeout(res, 600));
      }
      const r = await apiFetch('/api/wallet/auto-reload', { method: 'POST', body: JSON.stringify({ action: 'trigger' }) });
      showToast(`Auto-reload ${r.status === 'succeeded' ? 'charged ✓' : `status=${r.status}`} — webhook will credit the wallet shortly.`);
      setTimeout(load, 2500);
    } catch (err) { showToast(`Auto-reload demo failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  const resetDemo = async () => {
    if (!window.confirm('Refund all simulated debits back to the balance? Real top-ups stay untouched.')) return;
    setBusy(true);
    try {
      const r = await apiFetch('/api/wallet', { method: 'POST', body: JSON.stringify({ action: 'simulate_reset' }) });
      setData(r);
      showToast(r.refunded_cents > 0 ? `Refunded ${fmtUSD(r.refunded_cents)}.` : 'Nothing to reset — no simulated debits.');
    } catch (err) { showToast(`Reset failed: ${err.message}`); }
    finally { setBusy(false); }
  };

  // Hooks must be called unconditionally — keep these BEFORE the
  // early returns. Default to 0 while data is loading so the first
  // tween (loading → real balance) gracefully animates in.
  const tweenedBalance = useTweenedBalance(data?.balance_cents ?? 0);
  const flashTone      = useChangeFlash(data?.balance_cents ?? 0);

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading AI Wallet…</div>;
  if (loadError) return (
    <div className="text-sm p-4 border border-rose-200 bg-rose-50 rounded-lg">
      <div className="font-bold text-rose-800 mb-1">AI Wallet failed to load</div>
      <div className="text-xs text-rose-700 mb-2">{loadError}</div>
      <button onClick={load} className="text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded">Retry</button>
    </div>
  );
  if (!data)   return null;

  const isLow      = data.balance_cents < data.low_threshold_cents;
  const isEmpty    = data.balance_cents <= 0;
  // "Remaining from last top-up" — divides current balance by the
  // amount of the MOST RECENT top-up (not lifetime). The old math
  // divided by lifetime topups so the percent shrunk toward 0 after
  // every refill even when the balance was healthy. Falls back to
  // lifetime when last_topup is missing (older accounts that haven't
  // hit the new endpoint shape yet).
  const refillDenom = Number(data.last_topup_cents) > 0
    ? Number(data.last_topup_cents)
    : Number(data.lifetime_topup_cents) > 0 ? Number(data.lifetime_topup_cents) : 0;
  const refillPct  = refillDenom > 0
    ? Math.max(0, Math.min(100, Math.round((data.balance_cents / refillDenom) * 100)))
    : null;
  const demoMode   = !!data.demo_enabled; // server-derived; matches SFA_DEMO_WALLET env
  const spend      = data.spend_breakdown || { calling_cents: 0, calling_units: 0, texting_cents: 0, texting_units: 0, other_spend_cents: 0 };
  const totalSpend = (spend.calling_cents || 0) + (spend.texting_cents || 0) + (spend.other_spend_cents || 0);
  const pctCalling = totalSpend > 0 ? (spend.calling_cents / totalSpend) * 100 : 0;
  const pctTexting = totalSpend > 0 ? (spend.texting_cents / totalSpend) * 100 : 0;
  const pctOther   = totalSpend > 0 ? (spend.other_spend_cents / totalSpend) * 100 : 0;

  return (
    <div className="space-y-4">
      {/* HEADER CARD */}
      <div className={`rounded-2xl p-6 text-white relative overflow-hidden ${isEmpty ? 'bg-gradient-to-br from-rose-900 to-rose-700' : isLow ? 'bg-gradient-to-br from-amber-800 to-orange-700' : 'bg-gradient-to-br from-slate-900 to-emerald-900'}`}>
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="flex items-center gap-2 text-emerald-200 text-[0.55rem] font-black uppercase tracking-[0.3em]"><Wallet size={11}/> AI Wallet</div>
            <div className="text-emerald-300/70 text-xs mt-1">Six Figure Agent OS · combined calling + texting credits</div>
            <div className="text-[10px] uppercase tracking-wider text-emerald-300/60 mt-3">Available balance</div>
            <div className={`text-4xl font-black mt-0.5 transition-all duration-300 ${flashTone === 'debit' ? 'text-rose-200 drop-shadow-[0_0_12px_rgba(244,63,94,0.7)]' : flashTone === 'credit' ? 'text-emerald-200 drop-shadow-[0_0_12px_rgba(16,185,129,0.7)]' : ''}`}>
              {fmtUSD(tweenedBalance)}
            </div>
            {isLow && !isEmpty && <div className="mt-1 text-xs text-amber-200 font-bold uppercase tracking-wider">Low balance</div>}
            {isEmpty && <div className="mt-1 text-xs text-rose-200 font-bold uppercase tracking-wider">Wallet empty · AI calling/texting paused</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300/60">Remaining from last top-up</div>
            <div className="text-2xl font-bold">{refillPct == null ? '—' : `${refillPct}%`}</div>
            <div className="mt-3 text-xs space-y-0.5 text-emerald-100/90">
              <div>Calling <span className="font-bold">${(data.pricing.call_per_min_cents/100).toFixed(2)}/min</span></div>
              <div>Texting <span className="font-bold">${(data.pricing.sms_per_msg_cents/100).toFixed(2)}/msg</span></div>
            </div>
          </div>
        </div>
        {isLow && (
          <div className="mt-4 bg-amber-100/15 border border-amber-200/30 rounded-lg p-3 flex items-start gap-2 text-amber-100 text-xs">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0"/>
            <div>Low balance. Top up to keep your AI calling & texting running without interruption.</div>
          </div>
        )}
      </div>

      {/* DEMO — top of the page so it's the first thing the operator
          sees on a fresh wallet. Server-gated via demo_enabled (mirrors
          SFA_DEMO_WALLET); harmless when off.
          Three layers:
            1. Scenarios row — dramatic, demos the moments the blurb
               promises (low balance / hard stop / auto-reload).
            2. Real-agent moments — each button is a real workflow that
               fires multiple debits serially so the ledger fills row by
               row, with per-step toasts narrating spend.
            3. Raw debits — kept at the bottom for spec-readers who
               want to verify per-unit pricing exactly. */}
      {demoMode && (
        <div className="bg-amber-50 border-2 border-dashed border-amber-300 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-sm font-bold text-amber-900">Demo — watch it spend down</h3>
              <p className="text-xs text-amber-800/80 mt-1">Every button below is a real debit against this wallet. Pricing, low-balance ribbon, hard-stop, and auto-reload all behave like production.</p>
            </div>
            <button onClick={resetDemo} disabled={busy} className="text-xs font-bold uppercase tracking-wider bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 rounded-md px-3 py-1.5 disabled:opacity-50">↻ Reset demo</button>
          </div>

          {/* Scenarios — the dramatic moments. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900/70 mb-1.5">Trigger the moments</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <ScenarioBtn
                onClick={drainToLow} disabled={busy}
                tone="amber"
                title="🚨 Drain to low balance"
                desc="Drops to just under the threshold. Watch the orange Low-balance state light up."
              />
              <ScenarioBtn
                onClick={drainPastZero} disabled={busy}
                tone="rose"
                title="⛔ Hard stop at $0"
                desc="Drains to zero, then tries one more call. Server returns 402 — production behavior."
              />
              <ScenarioBtn
                onClick={triggerAutoReload} disabled={busy}
                tone="emerald"
                title="⚡ Trigger auto-reload"
                desc={data.has_payment_method && data.auto_topup_enabled
                  ? 'Drains under threshold, then runs the saved card. Reload lands in the ledger.'
                  : (data.has_payment_method ? 'Enable Auto-reload below first.' : 'Save a card first (top up once).')}
              />
            </div>
          </div>

          {/* Real-agent moments — pace + drum-roll. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900/70 mb-1.5">What does it cost to…</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AGENT_SCENARIOS.map(sc => (
                <AgentScenarioBtn
                  key={sc.key}
                  scenario={sc}
                  pricing={data.pricing}
                  disabled={busy}
                  onClick={() => runScenario(sc)}
                />
              ))}
            </div>
          </div>

          {/* Raw debits — keep for transparency. */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-900/70 mb-1.5">Or fire a raw debit</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <DemoBtn onClick={() => simulate('call', 1)}   disabled={busy} preview={`1 min × $${(data.pricing.call_per_min_cents/100).toFixed(2)}`}>📞 1-min call</DemoBtn>
              <DemoBtn onClick={() => simulate('call', 5)}   disabled={busy} preview={`5 min × $${(data.pricing.call_per_min_cents/100).toFixed(2)}`}>📞 5-min call</DemoBtn>
              <DemoBtn onClick={() => simulate('sms', 10)}   disabled={busy} preview={`10 msg × $${(data.pricing.sms_per_msg_cents/100).toFixed(2)}`}>💬 10 texts</DemoBtn>
              <DemoBtn onClick={() => simulate('sms', 100)}  disabled={busy} preview={`100 msg × $${(data.pricing.sms_per_msg_cents/100).toFixed(2)}`}>💬 100 texts</DemoBtn>
            </div>
          </div>
        </div>
      )}

      {/* ADD FUNDS */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-slate-900">Add funds</h3>
          <button onClick={load} className="text-slate-400 hover:text-slate-700" title="Refresh"><RefreshCcw size={14}/></button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {[5000, 10000, 25000, 50000].map(c => (
            <button key={c} onClick={() => topUp(c)} disabled={busy}
              className="border-2 border-slate-200 hover:border-emerald-600 hover:bg-emerald-50 text-slate-900 font-bold rounded-lg py-3 disabled:opacity-50">
              ${(c/100).toFixed(0)}
            </button>
          ))}
          <div className="border-2 border-slate-200 rounded-lg p-2 flex items-center gap-1">
            <span className="text-slate-500 text-sm">$</span>
            <input type="number" value={custom} onChange={e => setCustom(e.target.value)} placeholder="Other amount" min="50"
              className="w-full text-sm font-bold text-slate-900 focus:outline-none"/>
            <button onClick={() => topUp(Math.round(Number(custom) * 100))} disabled={busy || !custom}
              className="text-xs font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 disabled:opacity-50">Add</button>
          </div>
        </div>
        <div className="mt-2 text-[11px] text-slate-500">
          Minimum first load $50. Credits never expire and are non-refundable once added. Charged securely via Stripe.
        </div>
      </div>

      {/* AUTO-RELOAD */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-emerald-600"/>
            <h3 className="text-sm font-bold text-slate-900">Auto-reload</h3>
            <span className="text-xs text-slate-500">Never run dry</span>
          </div>
          <button onClick={() => setShowSettings(s => !s)} className="text-xs font-bold uppercase text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
            <Settings size={11}/> {showSettings ? 'Hide' : 'Settings'}
          </button>
        </div>
        <p className="text-xs text-slate-500">Refill automatically when your balance gets low.</p>

        {data.has_payment_method ? (
          <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
            <CheckCircle size={12}/> Saved card on file — auto-reload {data.auto_topup_enabled ? <strong>ON</strong> : <strong>OFF</strong>}
            {data.last_auto_reload_at && <span className="text-slate-400">· last reload {fmtAgo(data.last_auto_reload_at)}</span>}
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2 text-xs text-amber-700">
            <AlertTriangle size={12}/> No saved card. Top up once to enable auto-reload.
          </div>
        )}
        {data.last_auto_reload_error && (
          <div className="mt-2 text-xs text-rose-700">Last error: {data.last_auto_reload_error}</div>
        )}

        {showSettings && autoRel && (
          <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
            <label className="text-xs text-slate-700 inline-flex items-center gap-2">
              <input type="checkbox" checked={autoRel.enabled} onChange={e => setAutoRel({ ...autoRel, enabled: e.target.checked })}/>
              <span>Enable auto-reload</span>
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">When balance below</label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 text-sm">$</span>
                  <input type="number" value={autoRel.threshold} onChange={e => setAutoRel({ ...autoRel, threshold: Number(e.target.value) || 0 })}
                    className="w-24 border border-slate-300 rounded px-2 py-1 text-sm"/>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-slate-500">Reload amount</label>
                <div className="flex items-center gap-1">
                  <span className="text-slate-500 text-sm">$</span>
                  <input type="number" value={autoRel.amount} onChange={e => setAutoRel({ ...autoRel, amount: Number(e.target.value) || 0 })}
                    className="w-24 border border-slate-300 rounded px-2 py-1 text-sm"/>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveSettings} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-bold px-4 py-2 rounded-md">Save</button>
              {data.has_payment_method && <button onClick={testAutoReload} disabled={busy} className="text-sm font-bold text-slate-700 hover:text-slate-900 px-3 py-2 border border-slate-300 rounded-md">Test auto-reload now</button>}
            </div>
          </div>
        )}
      </div>

      {/* WHERE THE MONEY GOES */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-slate-900">Where the money goes</h3>
          <div className="text-xs text-slate-500">Lifetime spent: <strong>{fmtUSD(totalSpend)}</strong></div>
        </div>
        {totalSpend === 0 ? (
          <div className="text-xs text-slate-500 py-2">No spend yet — every dollar you load goes into AI calling or texting. Pricing: <strong>${(data.pricing.call_per_min_cents/100).toFixed(2)}/min</strong> for calls, <strong>${(data.pricing.sms_per_msg_cents/100).toFixed(2)}/msg</strong> for texts.</div>
        ) : (
          <>
            <div className="flex h-2 rounded overflow-hidden bg-slate-100 mt-1">
              <div style={{ width: `${pctCalling}%` }} className="bg-sky-600" title={`Calling ${pctCalling.toFixed(0)}%`}/>
              <div style={{ width: `${pctTexting}%` }} className="bg-emerald-600" title={`Texting ${pctTexting.toFixed(0)}%`}/>
              <div style={{ width: `${pctOther}%` }} className="bg-slate-400" title={`Other ${pctOther.toFixed(0)}%`}/>
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              <SpendTile color="sky"     label="AI calling" cents={spend.calling_cents} units={spend.calling_units} unitLabel="min" rate={data.pricing.call_per_min_cents}/>
              <SpendTile color="emerald" label="AI texting" cents={spend.texting_cents} units={spend.texting_units} unitLabel="msg" rate={data.pricing.sms_per_msg_cents}/>
              <SpendTile color="slate"   label="Other"      cents={spend.other_spend_cents} units={null} unitLabel="" rate={0}/>
            </div>
          </>
        )}
      </div>

      {/* ACTIVITY — collapsed by default. Operators on a busy wallet
          (dozens of debits per day) don't want the full ledger
          dominating the screen on every visit. The summary line +
          count is enough; click the disclosure to expand. */}
      <ActivityLedger
        rows={data.ledger}
        lifetimeSpend={data.lifetime_spend_cents}
        lifetimeTopup={data.lifetime_topup_cents}
      />

    </div>
  );
}

// Activity ledger — collapsed dropdown with summary header. Operators
// see the spent/loaded totals at a glance; click to expand the full
// row list. Uses native <details> so keyboard accessibility + screen
// readers work without extra wiring.
function ActivityLedger({ rows = [], lifetimeSpend = 0, lifetimeTopup = 0 }) {
  const [open, setOpen] = React.useState(false);
  const count = rows.length;
  const summary = count === 0
    ? 'No activity yet'
    : `${count} ${count === 1 ? 'entry' : 'entries'}`;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <ChevronDown
            size={16}
            className={`text-slate-500 transition-transform shrink-0 ${open ? 'rotate-0' : '-rotate-90'}`}
          />
          <h3 className="text-sm font-bold text-slate-900">Activity</h3>
          <span className="text-xs text-slate-500">· {summary}</span>
        </div>
        <div className="text-xs text-slate-500 shrink-0 hidden sm:block">
          Spent: <strong>{fmtUSD(lifetimeSpend)}</strong> · Loaded: <strong>{fmtUSD(lifetimeTopup)}</strong>
        </div>
      </button>
      {open && (
        count === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6 border-t border-slate-100">No activity yet.</div>
        ) : (
          <div className="divide-y divide-slate-100 border-t border-slate-100 max-h-[480px] overflow-y-auto">
            {rows.map(row => (
              <div key={row.id} className="px-5 py-2 flex items-center justify-between text-sm gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900">{row.label}</div>
                  {row.breakdown && <div className="text-[11px] text-slate-600 font-mono">{row.breakdown}</div>}
                  <div className="text-[11px] text-slate-400">{fmtAgo(row.created_at)} · balance after {fmtUSD(row.balance_after)}</div>
                </div>
                <div className={`font-bold whitespace-nowrap ${row.delta_cents >= 0 ? 'text-emerald-700' : 'text-slate-700'}`}>
                  {row.delta_cents >= 0 ? '+' : ''}{fmtUSD(row.delta_cents)}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

function SpendTile({ color, label, cents, units, unitLabel, rate }) {
  const tone = {
    sky:     'border-sky-200     bg-sky-50     text-sky-900',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-900',
    slate:   'border-slate-200   bg-slate-50   text-slate-700',
  }[color] || '';
  return (
    <div className={`border rounded-lg p-3 ${tone}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5">${((cents || 0) / 100).toFixed(2)}</div>
      <div className="text-[11px] opacity-70 mt-0.5">
        {units != null ? `${units || 0} ${unitLabel}${(units === 1 ? '' : 's')}${rate ? ` × $${(rate/100).toFixed(2)}` : ''}` : (cents > 0 ? 'Manual/admin' : '—')}
      </div>
    </div>
  );
}

function DemoBtn({ onClick, disabled, preview, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 rounded-md px-3 py-2 text-left disabled:opacity-50">
      <div className="text-xs font-bold">{children}</div>
      <div className="text-[10px] text-amber-700/80 font-mono">{preview}</div>
    </button>
  );
}

function ScenarioBtn({ onClick, disabled, tone, title, desc }) {
  const tones = {
    amber:   'bg-white border-amber-400   hover:bg-amber-100   text-amber-900',
    rose:    'bg-white border-rose-400    hover:bg-rose-100    text-rose-900',
    emerald: 'bg-white border-emerald-400 hover:bg-emerald-100 text-emerald-900',
  };
  return (
    <button onClick={onClick} disabled={disabled}
      className={`text-left border-2 rounded-lg px-3 py-2.5 disabled:opacity-50 ${tones[tone] || tones.amber}`}>
      <div className="text-xs font-bold">{title}</div>
      <div className="text-[10px] opacity-80 mt-0.5 leading-snug">{desc}</div>
    </button>
  );
}

function AgentScenarioBtn({ scenario, pricing, onClick, disabled }) {
  const totalCents = scenarioCostCents(scenario.steps, pricing);
  const smsCount   = scenario.steps.filter(s => s.kind === 'sms').reduce((a, s) => a + s.units, 0);
  const callMin    = scenario.steps.filter(s => s.kind === 'call').reduce((a, s) => a + s.units, 0);
  return (
    <button onClick={onClick} disabled={disabled}
      className="bg-white border border-amber-300 hover:bg-amber-100 text-amber-900 rounded-lg px-3 py-2.5 text-left disabled:opacity-50">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-sm font-bold">{scenario.emoji} {scenario.title}</div>
        <div className="text-xs font-mono font-bold text-amber-900/90">{fmtUSD(totalCents)}</div>
      </div>
      <div className="text-[10px] text-amber-800/70 mt-0.5 leading-snug">{scenario.desc}</div>
      <div className="text-[10px] text-amber-700/60 font-mono mt-0.5">
        {smsCount > 0 && `${smsCount} text${smsCount === 1 ? '' : 's'}`}
        {smsCount > 0 && callMin > 0 && ' · '}
        {callMin > 0 && `${callMin} call min`}
      </div>
    </button>
  );
}

